import React, { useMemo } from 'react';
import { OptionType, SelectMenu } from '@fullstory/components';
import { Dashboard } from '@fullstory/proto/fs/services/dx-objects';
import { WELCOME_DASHBOARD_ID } from '../../utils';
import { useIntl } from 'react-intl';

type SelectMenuProps = Pick<React.ComponentProps<typeof SelectMenu>, 'id' | 'clearable'>;

const toOptions = (
  dashboards: Dashboard[],
  group: string,
  filterDashboards?: (value: Dashboard) => boolean,
  disableDashboards?: (value: Dashboard) => boolean,
): OptionType<Dashboard>[] =>
  dashboards
    // hide the welcome dashboard as you cannot add cards to it
    .filter(
      dashboard => dashboard.id !== WELCOME_DASHBOARD_ID && (filterDashboards?.(dashboard) ?? true),
    )
    .map(dashboard => ({
      key: dashboard.id as string,
      label: dashboard.name,
      value: dashboard,
      group,
      disabled: disableDashboards?.(dashboard),
    }));

export interface DashboardSelectMenuProps extends SelectMenuProps {
  dashboardsLoading: boolean;
  selected: Dashboard | null | undefined;
  onChange: (dashboard: Dashboard | null) => void;
  starredDashboards?: Dashboard[];
  recentDashboards?: Dashboard[];
  otherDashboards?: Dashboard[];
  filterDashboards?: (value: Dashboard) => boolean;
  disableDashboards?: (value: Dashboard) => boolean;
}

export const DashboardSelectMenu: React.FC<DashboardSelectMenuProps> = ({
  id,
  dashboardsLoading,
  selected,
  onChange,
  starredDashboards = [],
  recentDashboards = [],
  otherDashboards = [],
  clearable = false,
  filterDashboards,
  disableDashboards,
}) => {
  const intl = useIntl();
  const options = useMemo(
    () => [
      ...toOptions(
        recentDashboards,
        intl.formatMessage({ defaultMessage: 'Recent', id: 'iXpep1' }),
        filterDashboards,
        disableDashboards,
      ),
      ...toOptions(
        starredDashboards,
        intl.formatMessage({ defaultMessage: 'Starred', id: 'V7cUPv' }),
        filterDashboards,
        disableDashboards,
      ),
      ...toOptions(
        otherDashboards,
        intl.formatMessage({ defaultMessage: 'All Dashboards', id: 'EJC7Zf' }),
        filterDashboards,
        disableDashboards,
      ),
    ],
    [
      recentDashboards,
      intl,
      filterDashboards,
      disableDashboards,
      starredDashboards,
      otherDashboards,
    ],
  );

  const selectedOption = useMemo(() => {
    if (!selected) return undefined;
    return options.find(o => o.value === selected);
  }, [options, selected]);

  return (
    <SelectMenu<Dashboard>
      data-component="DashboardSelectMenu"
      id={id}
      placeholder={
        dashboardsLoading
          ? intl.formatMessage({ defaultMessage: 'Dashboards loading...', id: '1bQtFZ' })
          : intl.formatMessage({ defaultMessage: 'Select Dashboard', id: 'wYgs8A' })
      }
      variant="outline"
      fullWidth
      filterable
      filterPlaceholder={intl.formatMessage({ defaultMessage: 'Find Dashboard', id: 'jgIo5W' })}
      menuHeight={385}
      selected={selectedOption}
      options={options}
      onChange={option => onChange(option?.value || null)}
      clearable={clearable}
    />
  );
};
